import { InsightsPortalSchema } from 'domains/insights-portal/api'
import {
  type createFilterParamConfig,
  transformFilterParamsToApiFilterSelectionsFactory,
} from 'domains/insights-portal/schemas/utils/insightsPortalSchemas.utils'
import {
  OrderDeliveryGroupBys,
  OrderDeliveryMetrics,
} from 'domains/insights-portal/schemas/order-deliveries/insightsPortalOrderDeliveries.types'
import { orderDeliveriesSchemaFilterParamsConfig } from 'domains/insights-portal/schemas/order-deliveries/insightsPortalOrderDeliveries.helpers'
import {
  OrderItemGroupBys,
  OrderItemsMetrics,
} from 'domains/insights-portal/schemas/order-items/insightsPortalOrderItems.types'
import { orderItemsSchemaFilterParamsConfig } from 'domains/insights-portal/schemas/order-items/insightsPortalOrderItemsSchema.helpers'
import {
  SearchTermGroupBys,
  SearchTermMetrics,
} from 'domains/insights-portal/schemas/search-term/insightsSearchTerm.types'
import { searchTermSchemaFilterParamsConfig } from 'domains/insights-portal/schemas/search-term/insightsSearchTermSchema.hooks'
import {
  AggCampaignGroupBys,
  AggCampaignMetrics,
} from 'domains/insights-portal/schemas/agg-warehouse-location-campaign-performance/insightsPortalAggCampaign.types'
import { aggCampaignSchemaFilterParamsConfig } from 'domains/insights-portal/schemas/agg-warehouse-location-campaign-performance/insightsPortalAggCampaignSchema.hooks'
import {
  SearchTermStitchSearchTermByTermBenchmarkHistoricalGrowthGroupBys,
  SearchTermStitchSearchTermByTermBenchmarkHistoricalGrowthMetrics,
} from 'domains/insights-portal/schemas/search-term-stitch-search-term-by-term-benchmark-historical-growth/searchTermStitchSearchTermByTermBenchmarkHistoricalGrowth.types'
import {
  OrderItemStitchProductByCategoryBenchmarkHistoricalGrowthGroupBys,
  OrderItemStitchProductByCategoryBenchmarkHistoricalGrowthMetrics,
} from 'domains/insights-portal/schemas/order-item-stitch-product-by-category-benchmark-historical-growth/orderItemStitchProductByCategoryBenchmarkHistoricalGrowth.types'
import { ImageCoverageMetrics } from 'domains/insights-portal/schemas/image-coverage/insightsPortalImageCoverage.types'
import { inventoryIntelligenceOosGroupBys } from 'domains/insights-portal/schemas/inventory-intelligence-oos-signals-agg/insightsPortalInventoryIntelligenceOosSignal.types'
import { inventoryIntelligenceOosSignalsSchemaFilterParamsConfig } from 'domains/insights-portal/schemas/inventory-intelligence-oos-signals-agg/insightsPortalInventoryIntelligenceOosSignalSchema.helpers'
import {
  MissingImage90DayRetailerInsightsGroupBys,
  MissingImage90DayRetailerInsightsMetrics,
} from 'domains/insights-portal/schemas/missing-image-90-day-retailer-insights/insightsPortalMissingImage90DayRetailerInsights.types'
import { missingImage90DayRetailerInsightsSchemaFilterParamsConfig } from 'domains/insights-portal/schemas/missing-image-90-day-retailer-insights/insightsPortalMissingImage90DayRetailerInsightsSchema.hooks'
import {
  AggWarehousePerformanceMetrics,
  AggWarehousePerformanceGroupBys,
} from 'domains/insights-portal/schemas/aggregate-warehouse-performance/insightsPortalAggWarehousePerformance.types'
import { aggPerformanceSchemaFilterParamsConfig } from 'domains/insights-portal/schemas/aggregate-warehouse-performance/insightsPortalAggWarehousePerformanceSummary.hooks'
import {
  CollectionsGroupBys,
  CollectionsMetrics,
} from 'domains/insights-portal/schemas/collections/insightsPortalCollections.types'
import { collectionSchemaFilterParamsConfig } from 'domains/insights-portal/schemas/collections/insightsPortalCollectionsSchema.hooks'
/* 
  This function is used to define the configuration for a schema. It just returns what was passed into it
  but it is being used to ensure stricter generic typing which can't be done with a plain Interface or Object type
*/
export function createSchemaConfig<
  TMetrics extends Record<string, string>,
  TGroupBys extends Record<string, string>
>(arg: {
  metrics: TMetrics
  groupBys: TGroupBys
  filterConfigs: ReturnType<typeof createFilterParamConfig>[]
  transformFilterParamsToApiFilterSelections: ReturnType<
    typeof transformFilterParamsToApiFilterSelectionsFactory
  >
}) {
  return arg
}

/* 
  This Object should contain the configuration for every schema being used to build insights widgets.
  This makes it so an engineer only needs to define the schema name then the widget will know how
  to handle the filters using the filterConfig defined here.

  Having a central schema config can also allow us to define default Display Name and formatters for each metric.
  And should allow better typing on widgets to constrain schemas to only metrics/groupBys they have, instead of a generic string
*/
export const insightsSchemaConfigs = {
  [InsightsPortalSchema.OrderDelivery]: createSchemaConfig({
    metrics: OrderDeliveryMetrics,
    groupBys: OrderDeliveryGroupBys,
    filterConfigs: orderDeliveriesSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      orderDeliveriesSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.OrderDeliveryHistoricalGrowth]: createSchemaConfig({
    metrics: OrderDeliveryMetrics,
    groupBys: OrderDeliveryGroupBys,
    filterConfigs: orderDeliveriesSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      orderDeliveriesSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.ImageCoverage]: createSchemaConfig({
    metrics: ImageCoverageMetrics,
    groupBys: {},
    filterConfigs: orderDeliveriesSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      orderDeliveriesSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.InventoryIntelligenceOosSignalsAgg]: createSchemaConfig({
    metrics: {},
    groupBys: inventoryIntelligenceOosGroupBys,
    filterConfigs: inventoryIntelligenceOosSignalsSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      inventoryIntelligenceOosSignalsSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.ImageCoverageHistoricalGrowth]: createSchemaConfig({
    metrics: ImageCoverageMetrics,
    groupBys: {},
    filterConfigs: orderDeliveriesSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      orderDeliveriesSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.OrderItem]: createSchemaConfig({
    metrics: OrderItemsMetrics,
    groupBys: OrderItemGroupBys,
    filterConfigs: orderItemsSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      orderItemsSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.OrderItemHistoricalGrowth]: createSchemaConfig({
    metrics: OrderItemsMetrics,
    groupBys: OrderItemGroupBys,
    filterConfigs: orderItemsSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      orderItemsSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.SearchTerm]: createSchemaConfig({
    metrics: SearchTermMetrics,
    groupBys: SearchTermGroupBys,
    filterConfigs: searchTermSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      searchTermSchemaFilterParamsConfig
    ),
  }),

  [InsightsPortalSchema.SearchTermHistoricalGrowth]: createSchemaConfig({
    metrics: SearchTermMetrics,
    groupBys: SearchTermGroupBys,
    filterConfigs: searchTermSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      searchTermSchemaFilterParamsConfig
    ),
  }),

  [InsightsPortalSchema.SearchTermStitchSearchTermByTermBenchmarkHistoricalGrowth]:
    createSchemaConfig({
      metrics: SearchTermStitchSearchTermByTermBenchmarkHistoricalGrowthMetrics,
      groupBys: SearchTermStitchSearchTermByTermBenchmarkHistoricalGrowthGroupBys,
      filterConfigs: searchTermSchemaFilterParamsConfig,
      transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
        searchTermSchemaFilterParamsConfig
      ),
    }),
  [InsightsPortalSchema.OrderItemStitchProductByCategoryBenchmarkHistoricalGrowth]:
    createSchemaConfig({
      metrics: OrderItemStitchProductByCategoryBenchmarkHistoricalGrowthMetrics,
      groupBys: OrderItemStitchProductByCategoryBenchmarkHistoricalGrowthGroupBys,
      filterConfigs: searchTermSchemaFilterParamsConfig,
      transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
        orderItemsSchemaFilterParamsConfig
      ),
    }),
  [InsightsPortalSchema.AggWarehouseLocationCampaignPerformance]: createSchemaConfig({
    metrics: AggCampaignMetrics,
    groupBys: AggCampaignGroupBys,
    filterConfigs: aggCampaignSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      aggCampaignSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.AggMerchCollections]: createSchemaConfig({
    metrics: CollectionsMetrics,
    groupBys: CollectionsGroupBys,
    filterConfigs: collectionSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      collectionSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.AggMerchCollectionsHistoricalGrowth]: createSchemaConfig({
    metrics: CollectionsMetrics,
    groupBys: CollectionsGroupBys,
    filterConfigs: collectionSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      collectionSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.AggWarehouseLocationCampaignPerformanceHistoricalGrowth]:
    createSchemaConfig({
      metrics: AggCampaignMetrics,
      groupBys: AggCampaignGroupBys,
      filterConfigs: aggCampaignSchemaFilterParamsConfig,
      transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
        aggCampaignSchemaFilterParamsConfig
      ),
    }),
  [InsightsPortalSchema.AggWarehouseLocationCampaignPerformanceStitchOrderDelivery]:
    createSchemaConfig({
      metrics: AggCampaignMetrics,
      groupBys: AggCampaignGroupBys,
      filterConfigs: aggCampaignSchemaFilterParamsConfig,
      transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
        aggCampaignSchemaFilterParamsConfig
      ),
    }),
  [InsightsPortalSchema.AggWarehouseLocationCampaignPerformanceStitchOrderDeliveryHistoricalGrowth]:
    createSchemaConfig({
      metrics: AggCampaignMetrics,
      groupBys: AggCampaignGroupBys,
      filterConfigs: aggCampaignSchemaFilterParamsConfig,
      transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
        aggCampaignSchemaFilterParamsConfig
      ),
    }),
  [InsightsPortalSchema.MissingImage_90DayRetailerInsights]: createSchemaConfig({
    metrics: MissingImage90DayRetailerInsightsMetrics,
    groupBys: MissingImage90DayRetailerInsightsGroupBys,
    filterConfigs: missingImage90DayRetailerInsightsSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      missingImage90DayRetailerInsightsSchemaFilterParamsConfig
    ),
  }),
  [InsightsPortalSchema.AggWarehouseLocationPerformanceHistoricalGrowth]: createSchemaConfig({
    metrics: AggWarehousePerformanceMetrics,
    groupBys: AggWarehousePerformanceGroupBys,
    filterConfigs: aggPerformanceSchemaFilterParamsConfig,
    transformFilterParamsToApiFilterSelections: transformFilterParamsToApiFilterSelectionsFactory(
      aggPerformanceSchemaFilterParamsConfig
    ),
  }),
} as const

export type InsightsSchemaConfigs = typeof insightsSchemaConfigs
